import { createContext, useContext, useEffect, useRef, useState } from "react";
import { Music, Pause, Play, SkipBack, SkipForward, Volume2, VolumeX, X, Youtube } from "lucide-react";
import { LINKTREE_URL, TRACKS } from "@/lib/tracks";
import { PawIcon, RADIO_LOGO } from "@/lib/brand";

export const SpotifyIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.52 17.31c-.22.35-.66.46-1.01.25-2.77-1.69-6.25-2.07-10.35-1.13-.39.09-.8-.16-.89-.55-.09-.39.16-.8.55-.89 4.11-.94 8.44-.48 11.45 1.31.34.21.46.66.25 1.01zm1.46-3.27c-.27.42-.82.55-1.24.28-3.17-1.95-8.01-2.52-11.76-1.38-.48.15-1-.13-1.15-.61-.15-.48.13-1 .61-1.15 4.31-1.31 9.65-.67 13.26 1.62.42.25.55.82.28 1.24zm.13-3.36C15.31 8.36 9.06 8.11 5.4 9.22c-.56.17-1.16-.15-1.33-.71-.17-.56.15-1.16.71-1.33 4.19-1.27 11.13-1.02 15.52 1.57.51.3.68.96.38 1.47-.3.5-.96.68-1.47.38z" />
    </svg>
);

const PlayerCtx = createContext(null);
export const usePlayer = () => useContext(PlayerCtx);

const fmt = (s) => {
    if (!Number.isFinite(s) || s < 0) return "0:00";
    const m = Math.floor(s / 60);
    return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

export function PlayerProvider({ children }) {
    const audioRef = useRef(null);
    const indexRef = useRef(-1);
    const volumeRef = useRef(0.9);
    const [index, setIndex] = useState(-1);
    const [playing, setPlaying] = useState(false);
    const [time, setTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [volume, setVolumeState] = useState(0.9);
    const [muted, setMuted] = useState(false);

    if (!audioRef.current) {
        audioRef.current = new Audio();
        audioRef.current.preload = "metadata";
        window.__purpAudio = audioRef.current;
    }

    const select = (i, autoplay = true) => {
        const t = TRACKS[i];
        if (!t) return;
        indexRef.current = i;
        setIndex(i);
        setTime(0);
        const a = audioRef.current;
        a.src = t.src;
        a.volume = volumeRef.current;
        if (autoplay) a.play().catch(() => {});
    };

    const toggle = () => {
        const a = audioRef.current;
        if (indexRef.current < 0) {
            if (TRACKS.length > 0) select(0);
            return;
        }
        if (a.paused) a.play().catch(() => {});
        else a.pause();
    };

    const step = (d) => {
        if (TRACKS.length === 0) return;
        select((indexRef.current + d + TRACKS.length) % TRACKS.length);
    };

    const close = () => {
        const a = audioRef.current;
        a.pause();
        a.removeAttribute("src");
        indexRef.current = -1;
        setIndex(-1);
        setTime(0);
        setDuration(0);
    };

    useEffect(() => {
        const a = audioRef.current;
        const onTime = () => setTime(a.currentTime);
        const onMeta = () => setDuration(a.duration);
        const onEnd = () => step(1);
        const onPlay = () => setPlaying(true);
        const onPause = () => setPlaying(false);
        a.addEventListener("timeupdate", onTime);
        a.addEventListener("loadedmetadata", onMeta);
        a.addEventListener("ended", onEnd);
        a.addEventListener("play", onPlay);
        a.addEventListener("pause", onPause);
        return () => {
            a.removeEventListener("timeupdate", onTime);
            a.removeEventListener("loadedmetadata", onMeta);
            a.removeEventListener("ended", onEnd);
            a.removeEventListener("play", onPlay);
            a.removeEventListener("pause", onPause);
        };
    }, []);

    const value = {
        tracks: TRACKS,
        track: index >= 0 ? TRACKS[index] : null,
        index,
        playing,
        time,
        duration,
        volume,
        muted,
        select,
        toggle,
        next: () => step(1),
        prev: () => step(-1),
        close,
        seek: (v) => {
            audioRef.current.currentTime = v;
            setTime(v);
        },
        setVolume: (v) => {
            volumeRef.current = v;
            audioRef.current.volume = v;
            if (v > 0 && audioRef.current.muted) {
                audioRef.current.muted = false;
                setMuted(false);
            }
            setVolumeState(v);
        },
        toggleMute: () => {
            const m = !muted;
            audioRef.current.muted = m;
            setMuted(m);
        },
    };

    return <PlayerCtx.Provider value={value}>{children}</PlayerCtx.Provider>;
}

const ctrlBtn =
    "w-9 h-9 rounded-full flex items-center justify-center text-slate-300 hover:text-purp-neon hover:bg-purp/10 transition-colors";

export function PlayerBar() {
    const p = usePlayer();
    if (!p.track) return null;
    const t = p.track;
    return (
        <>
            <div className="h-[132px] sm:h-20" aria-hidden="true" />
            <div className="fixed bottom-0 inset-x-0 z-[70] border-t border-purp/30 bg-obsidian/90 backdrop-blur-xl" data-testid="player-bar">
                <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3 md:gap-5">
                    <img
                        src={t.cover || RADIO_LOGO}
                        alt=""
                        className="w-11 h-11 rounded-lg object-cover border border-purp/40 shrink-0"
                        data-testid="player-cover"
                    />
                    <div className="min-w-0 w-28 sm:w-40 shrink-0">
                        <p className="font-display font-bold uppercase text-sm truncate" data-testid="player-track-title">{t.title}</p>
                        <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500 truncate" data-testid="player-track-artist">{t.artist}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                        <button onClick={p.prev} className={ctrlBtn} aria-label="Previous track" data-testid="player-prev">
                            <SkipBack className="w-4 h-4" />
                        </button>
                        <button
                            onClick={p.toggle}
                            aria-label={p.playing ? "Pause" : "Play"}
                            data-testid="player-play-pause"
                            className="w-10 h-10 rounded-full bg-purp text-obsidian flex items-center justify-center hover:shadow-[0_0_24px_rgba(168,85,247,0.6)] transition-shadow"
                        >
                            {p.playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                        </button>
                        <button onClick={p.next} className={ctrlBtn} aria-label="Next track" data-testid="player-next">
                            <SkipForward className="w-4 h-4" />
                        </button>
                    </div>
                    <div className="hidden sm:flex items-center gap-2 flex-1 min-w-0">
                        <span className="font-mono text-[10px] text-slate-500 w-9 text-right shrink-0" data-testid="player-time">{fmt(p.time)}</span>
                        <input
                            type="range"
                            min={0}
                            max={Number.isFinite(p.duration) ? p.duration : 0}
                            step={0.1}
                            value={p.time}
                            onChange={(e) => p.seek(Number(e.target.value))}
                            className="flex-1 accent-purp h-1 cursor-pointer"
                            aria-label="Seek"
                            data-testid="player-seek"
                        />
                        <span className="font-mono text-[10px] text-slate-500 w-9 shrink-0" data-testid="player-duration">{fmt(p.duration)}</span>
                    </div>
                    <div className="hidden md:flex items-center gap-1.5 shrink-0">
                        <button onClick={p.toggleMute} className={ctrlBtn} aria-label={p.muted ? "Unmute" : "Mute"} data-testid="player-mute">
                            {p.muted || p.volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                        </button>
                        <input
                            type="range"
                            min={0}
                            max={1}
                            step={0.05}
                            value={p.muted ? 0 : p.volume}
                            onChange={(e) => p.setVolume(Number(e.target.value))}
                            className="w-20 accent-purp h-1 cursor-pointer"
                            aria-label="Volume"
                            data-testid="player-volume"
                        />
                    </div>
                    <button onClick={p.close} className={`${ctrlBtn} ml-auto sm:ml-0`} aria-label="Close player" data-testid="player-close">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="sm:hidden px-4 pb-3 flex items-center gap-2">
                    <span className="font-mono text-[10px] text-slate-500 w-9 text-right shrink-0">{fmt(p.time)}</span>
                    <input
                        type="range"
                        min={0}
                        max={Number.isFinite(p.duration) ? p.duration : 0}
                        step={0.1}
                        value={p.time}
                        onChange={(e) => p.seek(Number(e.target.value))}
                        className="flex-1 accent-purp h-1 cursor-pointer"
                        aria-label="Seek"
                        data-testid="player-seek-mobile"
                    />
                    <span className="font-mono text-[10px] text-slate-500 w-9 shrink-0">{fmt(p.duration)}</span>
                </div>
            </div>
        </>
    );
}

const linkPill =
    "rounded-full border border-white/15 px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.15em] text-slate-300 hover:text-purp-neon hover:border-purp/50 transition-colors inline-flex items-center gap-1.5";

function PlaylistButton({ testid }) {
    return (
        <a href={LINKTREE_URL} target="_blank" rel="noopener noreferrer" className={linkPill} data-testid={testid}>
            <SpotifyIcon className="w-3.5 h-3.5" />
            <Music className="w-3.5 h-3.5" />
            <Youtube className="w-3.5 h-3.5" />
            <span>Add to Your Playlist</span>
        </a>
    );
}

export function RadioTracks() {
    const p = usePlayer();
    if (p.tracks.length === 0) {
        return (
            <div className="mt-8 rounded-2xl border border-dashed border-purp/30 bg-panel/50 p-6 flex items-center gap-4 max-w-lg" data-testid="radio-empty-state">
                <PawIcon className="w-6 h-6 text-purp/50 shrink-0" />
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 leading-relaxed">
                    First broadcast loading — original music &amp; MY 5 audio drops land here.
                </p>
            </div>
        );
    }
    return (
        <div className="mt-8 space-y-3 max-w-xl" data-testid="radio-tracklist">
            {p.tracks.map((t, i) => {
                const active = p.index === i;
                return (
                    <div
                        key={t.id}
                        data-testid={`radio-track-${t.id}`}
                        className={`flex flex-wrap items-center gap-3 sm:gap-4 rounded-2xl border p-3 transition-colors duration-300 ${active ? "border-purp/60 bg-purp/10" : "border-white/10 bg-panel/60 hover:border-purp/40"}`}
                    >
                        <button
                            onClick={() => (active ? p.toggle() : p.select(i))}
                            aria-label={active && p.playing ? `Pause ${t.title}` : `Play ${t.title}`}
                            data-testid={`radio-track-play-${t.id}`}
                            className="w-11 h-11 rounded-xl bg-purp text-obsidian flex items-center justify-center shrink-0 hover:shadow-[0_0_20px_rgba(168,85,247,0.6)] transition-shadow"
                        >
                            {active && p.playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                        </button>
                        <img src={t.cover || RADIO_LOGO} alt="" className="w-11 h-11 rounded-lg object-cover border border-purp/30 shrink-0" />
                        <div className="min-w-0 flex-1">
                            <p className="font-display font-bold uppercase text-base truncate">{t.title}</p>
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 truncate">
                                {t.artist} · {t.kind === "my5-drop" ? "MY 5 Drop" : "Single"}
                            </p>
                        </div>
                        <div className="w-full sm:w-auto flex items-center shrink-0">
                            <PlaylistButton testid={`radio-link-${t.id}-playlist`} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
